<script lang="ts">
const SIDE_NON_SELECTED = -1 as Side; // TODO: this is hacky
const MIN_PERCENT_COLLATERAL = 100;
const MAX_PERCENT_COLLATERAL = 199;
const NSCC_DIVIDEND_REQUIRED = new Decimal(100);
</script>

<script setup lang="ts">
import {
  Agreement,
  CollateralMethod,
  CollateralType,
  CreateAgreementRequest,
  CreateAgreementRequestSchema,
  Currency,
  Data,
  DeliveryType,
  ModifyAgreementRequest,
  ModifyAgreementRequestSchema,
  RoundingRule,
  SettlementSystem,
  SettlementType,
  Side,
  TermType,
  availableCollateralMethods,
  availableCollateralTypes,
  availableCurrencies,
  availableDeliveryTypes,
  availableRoundingRules,
  availableSettlementSystems,
  availableSettlementTypes,
  availableSides,
  availableTermTypes,
  clone,
  create,
  equals,
  formatSide,
  isMessage,
} from '@/connect';
import { UnwrapRef, computed, ref, shallowRef, watch, watchEffect } from 'vue';
import NumericInput from '@/modules/common/components/NumericInput.vue';
import { useAuthStore } from '@/store/auth-store';
import CounterpartySearch from '@/modules/user-accounts/components/CounterpartySearch.vue';
import { CompanyInfo } from '@/modules/common/models';
import { useRules } from '@/composables/vuetify-validation';
import Decimal from 'decimal.js';
import TextDatePicker from '@/modules/common/components/TextDatePicker.vue';
import { useDate, useDecimal } from '@/modules/agreements/composables';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useUserAccountsService } from '@/modules/user-accounts/services/user-accounts-api.service';
import CompanyInfoDisplay from '@/modules/user-accounts/components/CompanyInfoDisplay.vue';
import { oppositeSide } from '@/utils/helpers/enums';
import { companySideFromSide } from '@/modules/user-accounts/helpers/user-accounts';
import { CompanySide } from '@/modules/user-accounts/types/user-accounts';

const props = defineProps<{
  source: Data<Agreement> | null;
  value: Data<CreateAgreementRequest> | Data<ModifyAgreementRequest> | null;
  reservedShortNames: Set<string>;
}>();

const emit = defineEmits<{
  (event: 'cancel'): void;
  (
    event: 'save',
    agreementRequest: UnwrapRef<Data<CreateAgreementRequest> | Data<ModifyAgreementRequest>>
  ): void;
}>();

const snackbarManager = useSnackbarManager();
const authStore = useAuthStore();

const agreementRequest = ref<Data<CreateAgreementRequest> | Data<ModifyAgreementRequest> | null>(
  props.value ?? create(CreateAgreementRequestSchema)
);
const counterpartySearchSide = computed<CompanySide>(() =>
  agreementRequest.value &&
  isMessage(agreementRequest.value, CreateAgreementRequestSchema) &&
  (agreementRequest.value.side as number) !== -1
    ? companySideFromSide(oppositeSide(agreementRequest.value.side))
    : 'ALL'
);

let createAgreementSnapshot = create(CreateAgreementRequestSchema);

watch(
  () => props.value,
  (request) => {
    agreementRequest.value = isMessage(request, CreateAgreementRequestSchema)
      ? clone(CreateAgreementRequestSchema, request)
      : isMessage(request, ModifyAgreementRequestSchema)
        ? clone(ModifyAgreementRequestSchema, request)
        : null;

    if (isMessage(agreementRequest.value, CreateAgreementRequestSchema)) {
      const isNotADuplicate = props.source === null;
      if (isNotADuplicate) {
        // preset new agreement values
        agreementRequest.value.counterpartyId = '';
        agreementRequest.value.side = SIDE_NON_SELECTED;
        agreementRequest.value.collateralType = CollateralType.CASH;
        agreementRequest.value.pricingCurrency = Currency.USD;
        agreementRequest.value.collateralCurrency = Currency.USD;
        agreementRequest.value.collateralMethod = CollateralMethod.TITLE_TRANSFER;
        agreementRequest.value.collateralRate = '0';
        agreementRequest.value.roundingRule = RoundingRule.NO_ROUNDING;
        agreementRequest.value.dividendRequired = '100';
        agreementRequest.value.billingCurrency = Currency.USD;
        agreementRequest.value.termType = TermType.NONE;
        agreementRequest.value.deliveryType = DeliveryType.DVP;
      }
      createAgreementSnapshot = clone(CreateAgreementRequestSchema, agreementRequest.value);
    }
  },
  { immediate: true }
);

const agreementCounterparty = shallowRef<null | CompanyInfo>(null);

// set the counterparty for the agreement based on the value if possible
watchEffect(async () => {
  if (!isMessage(props.value, CreateAgreementRequestSchema)) return;

  if (props.value.counterpartyId === '') {
    agreementCounterparty.value = null;
    return;
  }

  agreementCounterparty.value = await useUserAccountsService().fetchCompanyByDisplayId(
    props.value.counterpartyId
  );
});

// update the counterpartyId when the counterparty choice is changed
watchEffect(() => {
  if (isMessage(agreementRequest.value, CreateAgreementRequestSchema)) {
    agreementRequest.value.counterpartyId = agreementCounterparty.value?.displayId ?? '';
  }
});

let termStartDateBackup: string | undefined = undefined;
let termEndDateBackup: string | undefined = undefined;
watch(
  () => agreementRequest.value?.termType,
  (termTypeNew, termTypeOld) => {
    if (agreementRequest.value === null) return;
    if (termTypeNew === TermType.NONE) {
      termStartDateBackup = agreementRequest.value.termStartDate ?? termStartDateBackup;
      termEndDateBackup = agreementRequest.value.termEndDate ?? termEndDateBackup;
      agreementRequest.value.termStartDate = undefined;
      agreementRequest.value.termEndDate = undefined;
    }
    if (termTypeOld === TermType.NONE) {
      agreementRequest.value.termStartDate = termStartDateBackup;
      agreementRequest.value.termEndDate = termEndDateBackup;
    }
  }
);

function closeDialog() {
  emit('cancel');
}

function closeIfNoChanges() {
  if (!hasChanges.value) {
    closeDialog();
  }
}

const hasChanges = computed(() => {
  if (!agreementRequest.value || !props.value) {
    return false;
  }
  if (
    isMessage(agreementRequest.value, ModifyAgreementRequestSchema) &&
    isMessage(props.value, ModifyAgreementRequestSchema)
  ) {
    return !equals(ModifyAgreementRequestSchema, agreementRequest.value, props.value);
  }
  if (
    isMessage(agreementRequest.value, CreateAgreementRequestSchema) &&
    isMessage(props.value, CreateAgreementRequestSchema)
  ) {
    return !equals(CreateAgreementRequestSchema, agreementRequest.value, createAgreementSnapshot);
  }
  return true;
});

function save(request: UnwrapRef<Data<CreateAgreementRequest> | Data<ModifyAgreementRequest>>) {
  if (hasChanges.value) {
    emit('save', request);
  } else {
    snackbarManager.show({
      message: 'Agreements is already in the requested state.',
      color: 'info',
    });
  }

  closeDialog();
}

const isNewAgreement = computed(() =>
  isMessage(agreementRequest.value, CreateAgreementRequestSchema)
);
const independentAmountRate = useDecimal(agreementRequest, 'collateralRate', () => ({
  defaultGetValue: authStore.user?.companyPreferredIndependentAmountRate ?? 0,
  defaultSetModel: props.value?.collateralRate,
}));
const percentCollateral = computed<number | null>({
  get: () => independentAmountRate.value?.toNumber() ?? null,
  set: (value) => value === null || (independentAmountRate.value = new Decimal(value)),
});
const dividendRequired = useDecimal(agreementRequest, 'dividendRequired', () => ({
  defaultGetValue: 0,
  defaultSetModel: props.value?.dividendRequired,
}));
const balanceTarget = useDecimal(agreementRequest, 'balanceTarget', () => ({
  defaultGetValue: null,
  defaultSetModel: props.value?.balanceTarget,
}));
const termStartDate = useDate(agreementRequest, 'termStartDate');
const termEndDate = useDate(agreementRequest, 'termEndDate');
const balanceTargetVariance = useDecimal(agreementRequest, 'balanceTargetVariance', () => ({
  defaultGetValue: null,
  defaultSetModel: props.value?.balanceTargetVariance,
}));
const roundingRule = computed({
  get: () =>
    agreementRequest.value?.roundingRule ??
    (authStore.user?.companyPreferredRoundingRule !== undefined &&
    authStore.user.companyPreferredRoundingRule !== null
      ? RoundingRule[authStore.user.companyPreferredRoundingRule]
      : RoundingRule.NO_ROUNDING),
  set: (value) => agreementRequest.value && (agreementRequest.value.roundingRule = value),
});

// reset settlementType if the counterparty is changed to one which doesn't have the settlement type enabled
watch(agreementCounterparty, () => {
  if (
    agreementRequest.value !== null &&
    !isSettlementTypeAlreadySetInSource(agreementRequest.value.settlementType) &&
    (!isSettlementTypeEnabled(agreementRequest.value.settlementType) ||
      (!!agreementCounterparty.value &&
        !isSettlementTypePossibleForCounterparty(agreementRequest.value.settlementType)))
  ) {
    agreementRequest.value.settlementType = SettlementType.UNSPECIFIED;
  }
});

// settlement type options should only be settlement types that are enabled for us
// or if somehow an existing agreement already has a settlement type set
const settlementTypeOptions = computed(() => {
  return availableSettlementTypes
    .filter(({ id }) => isSettlementTypeAlreadySetInSource(id) || isSettlementTypeEnabled(id))
    .map(({ id, name }) => ({
      id,
      name,
      // disable options which aren't possible with the selected counterparty
      disabled:
        !isSettlementTypeAlreadySetInSource(id) && !isSettlementTypePossibleForCounterparty(id),
    }));
});

const isSettlementTypeNSCC = computed(
  () => agreementRequest.value?.settlementType === SettlementType.NSCC
);

const isCollateralTypeCash = computed(
  () => agreementRequest.value?.collateralType === CollateralType.CASH
);

const isSettlementTypeOCC = computed(
  () => agreementRequest.value?.settlementType === SettlementType.OCC
);

// when we switch to NSCC, force all NSCC-constrained fields and backup previous values
// when we switch away from NSCC, restore previous values
const nsccSettlementSystemBackup = shallowRef<SettlementSystem | null>(null);
const nsccPricingCurrencyBackup = shallowRef<Currency | null>(null);
const nsccBillingCurrencyBackup = shallowRef<Currency | null>(null);
const nsccDividendRequiredBackup = shallowRef<string | null>(null);
const nsccTermTypeBackup = shallowRef<TermType | null>(null);
const nsccCollateralTypeBackup = shallowRef<CollateralType | null>(null);
watch(
  isSettlementTypeNSCC,
  (isNSCC) => {
    if (agreementRequest.value === null) return;
    if (isNSCC) {
      nsccSettlementSystemBackup.value = agreementRequest.value?.settlementSystem ?? null;
      agreementRequest.value.settlementSystem = SettlementSystem.PROVABLE_MARKET;
      nsccPricingCurrencyBackup.value = agreementRequest.value.pricingCurrency ?? null;
      agreementRequest.value.pricingCurrency = Currency.USD;
      nsccBillingCurrencyBackup.value = agreementRequest.value.billingCurrency ?? null;
      agreementRequest.value.billingCurrency = Currency.USD;
      nsccDividendRequiredBackup.value = agreementRequest.value.dividendRequired ?? null;
      agreementRequest.value.dividendRequired = '100';
      nsccTermTypeBackup.value = agreementRequest.value.termType ?? null;
      agreementRequest.value.termType = TermType.NONE;
      nsccCollateralTypeBackup.value = agreementRequest.value.collateralType ?? null;
      agreementRequest.value.collateralType = CollateralType.CASH;
    } else {
      agreementRequest.value.settlementSystem = nsccSettlementSystemBackup.value ?? undefined;
      agreementRequest.value.pricingCurrency = nsccPricingCurrencyBackup.value ?? Currency.USD;
      agreementRequest.value.billingCurrency = nsccBillingCurrencyBackup.value ?? Currency.USD;
      agreementRequest.value.dividendRequired = nsccDividendRequiredBackup.value ?? '100';
      agreementRequest.value.termType = nsccTermTypeBackup.value ?? TermType.NONE;
      agreementRequest.value.collateralType = nsccCollateralTypeBackup.value ?? CollateralType.CASH;
    }
  },
  { immediate: true }
);

// when collateral type is CASH, force collateralCurrency=USD, collateralMethod=TITLE_TRANSFER, deliveryType=DVP
// when switching away from CASH, restore previous values
const collateralCurrencyBackup = shallowRef<Currency | null>(null);
const collateralMethodBackup = shallowRef<CollateralMethod | null>(null);
const deliveryTypeBackup = shallowRef<DeliveryType | null>(null);
watch(
  isCollateralTypeCash,
  (isCash) => {
    if (agreementRequest.value === null) return;
    if (isCash) {
      // backup and force constrained values
      collateralCurrencyBackup.value = agreementRequest.value?.collateralCurrency ?? null;
      agreementRequest.value.collateralCurrency = Currency.USD;
      collateralMethodBackup.value = agreementRequest.value?.collateralMethod ?? null;
      agreementRequest.value.collateralMethod = CollateralMethod.TITLE_TRANSFER;
      deliveryTypeBackup.value = agreementRequest.value?.deliveryType ?? null;
      agreementRequest.value.deliveryType = DeliveryType.DVP;
    } else {
      // restore previous values
      agreementRequest.value.collateralCurrency = undefined;
      agreementRequest.value.collateralMethod =
        collateralMethodBackup.value ?? CollateralMethod.PLEDGE;
      agreementRequest.value.deliveryType = deliveryTypeBackup.value ?? DeliveryType.DVP;
    }
  },
  { immediate: true }
);

// when settlement type is OCC, force billingCurrency=USD, dividendRequired=100, termType=NONE, collateralType=CASH
// when switching away from OCC, restore previous values
const occBillingCurrencyBackup = shallowRef<Currency | null>(null);
const occDividendRequiredBackup = shallowRef<string | null>(null);
const occTermTypeBackup = shallowRef<TermType | null>(null);
const occCollateralTypeBackup = shallowRef<CollateralType | null>(null);
watch(
  isSettlementTypeOCC,
  (isOCC) => {
    if (agreementRequest.value === null) return;
    if (isOCC) {
      occBillingCurrencyBackup.value = agreementRequest.value.billingCurrency ?? null;
      agreementRequest.value.billingCurrency = Currency.USD;
      occDividendRequiredBackup.value = agreementRequest.value.dividendRequired ?? null;
      agreementRequest.value.dividendRequired = '100';
      occTermTypeBackup.value = agreementRequest.value.termType ?? null;
      agreementRequest.value.termType = TermType.NONE;
      occCollateralTypeBackup.value = agreementRequest.value.collateralType ?? null;
      agreementRequest.value.collateralType = CollateralType.CASH;
    } else {
      agreementRequest.value.billingCurrency = occBillingCurrencyBackup.value ?? Currency.USD;
      agreementRequest.value.dividendRequired = occDividendRequiredBackup.value ?? '100';
      agreementRequest.value.termType = occTermTypeBackup.value ?? TermType.NONE;
      agreementRequest.value.collateralType = occCollateralTypeBackup.value ?? CollateralType.CASH;
    }
  },
  { immediate: true }
);

// convert empty strings to undefined for bookingAccount, tradeType and settlementSystem
// (because we don't want to save empty strings to the DB but leave them as null)
watch(
  () => agreementRequest.value?.bookingAccount,
  (value) => {
    if (agreementRequest.value && value === '') {
      agreementRequest.value.bookingAccount = undefined;
    }
  }
);

watch(
  () => agreementRequest.value?.tradeType,
  (value) => {
    if (agreementRequest.value && value === '') {
      agreementRequest.value.tradeType = undefined;
    }
  }
);

watch(
  () => agreementRequest.value?.settlementSystem,
  (value) => {
    if (agreementRequest.value && value === null) {
      agreementRequest.value.settlementSystem = undefined;
    }
  }
);

const profitCenter = computed({
  get: () => agreementRequest.value?.profitCenter ?? null,
  set: (v) => agreementRequest.value && (agreementRequest.value.profitCenter = v ?? undefined),
});

const collateralTypeOptions = computed(() =>
  availableCollateralTypes.map((collateralType) => ({
    ...collateralType,
    disabled:
      (isSettlementTypeNSCC.value || isSettlementTypeOCC.value) &&
      collateralType.id !== CollateralType.CASH,
  }))
);

const currencyOptions = computed(() =>
  availableCurrencies.map((currency) => ({
    ...currency,
    disabled:
      (isSettlementTypeNSCC.value || isSettlementTypeOCC.value) && currency.id !== Currency.USD,
  }))
);

const pricingCurrencyOptions = computed(() =>
  availableCurrencies.map((currency) => ({
    ...currency,
    disabled: isSettlementTypeNSCC.value && currency.id !== Currency.USD,
  }))
);

const collateralMethodOptions = computed(() =>
  availableCollateralMethods.map((collateralMethod) => ({
    ...collateralMethod,
    disabled:
      (isCollateralTypeCash.value || isSettlementTypeNSCC.value) &&
      collateralMethod.id !== CollateralMethod.TITLE_TRANSFER,
  }))
);

const deliveryTypeOptions = computed(() =>
  availableDeliveryTypes.map((deliveryType) => ({
    ...deliveryType,
    disabled:
      (isCollateralTypeCash.value || isSettlementTypeNSCC.value) &&
      deliveryType.id !== DeliveryType.DVP,
  }))
);

const settlementSystemOptions = computed(() =>
  availableSettlementSystems.map((settlementSystem) => ({
    ...settlementSystem,
    disabled:
      isSettlementTypeNSCC.value && settlementSystem.id !== SettlementSystem.PROVABLE_MARKET,
  }))
);

function isValueValidAndReadonly<T>(value: T, target: T) {
  if (!isSettlementTypeNSCC.value && !isSettlementTypeOCC.value) return false;
  if (value instanceof Decimal) {
    return value.eq(target as Decimal);
  }
  return value === target;
}

function isSettlementTypeEnabled(st: SettlementType): boolean {
  return authStore.user?.companyEnabledSettlementTypes.includes(st) ?? false;
}

function isSettlementTypePossibleForCounterparty(st: SettlementType): boolean {
  return agreementCounterparty.value?.enabledSettlementTypes?.includes(st) ?? true;
}

function isSettlementTypeAlreadySetInSource(st: SettlementType): boolean {
  return props.source?.settlementType === st;
}

function required<T>(value: T) {
  return (typeof value === 'string' ? !!value.trim() : !!value) || 'Required';
}

function checkForNSCCError(correctValue: string) {
  if (agreementRequest.value?.settlementType === SettlementType.NSCC) {
    return `Wrong value for Settlement Type NSCC (must be ${correctValue})`;
  }
  return true;
}

function checkForCashCollateralError(expectedLabel: string) {
  if (agreementRequest.value?.collateralType === CollateralType.CASH) {
    return `Must be ${expectedLabel} when collateral type is Cash`;
  }
  return true;
}

function checkForOCCError(expectedLabel: string) {
  if (agreementRequest.value?.settlementType === SettlementType.OCC) {
    return `Must be ${expectedLabel} when settlement type is OCC`;
  }
  return true;
}

const { rules, isValidating, validateAndSubmit, validate } = useRules<
  {
    shortName: string;
    side: Side | -1;
    percentCollateral: number | '' | null | Decimal;
    counterparty: CompanyInfo | null;
    settlementType: SettlementType;
    priceRoundingRules: RoundingRule;
    collateralType: CollateralType;
    pricingCurrency: Currency;
    billingCurrency: Currency;
    collateralCurrency: Currency;
    collateralMethod: CollateralMethod;
    dividendRequired: number | string | null | Decimal;
    termType: TermType;
    termStartDate: string;
    termEndDate: string;
    rwaBucket: string;
    bookingAccount: string;
    tradeType: string;
    deliveryType: DeliveryType;
    profitCenter: string;
  },
  { percentCollateral: Decimal | null }
>({
  shortName: {
    rules: [
      required,
      (value) =>
        (isMessage(agreementRequest.value, ModifyAgreementRequestSchema) &&
          props.source?.shortName === value) ||
        !props.reservedShortNames.has(value.trim().toUpperCase()) ||
        'Must be unique',
      (value) => value.length <= 25 || 'Must have at most 25 characters',
      (value) => !/['",]/.test(value) || 'Commas and quotes are not allowed',
    ],
  },
  side: { rules: [(value) => !isNewAgreement.value || value !== -1 || 'Required'] },
  percentCollateral: {
    transform: (value) => (value === null ? value : value === '' ? null : new Decimal(value)),
    rules: [
      required,
      (value) =>
        value?.gte(MIN_PERCENT_COLLATERAL) ||
        `Collateral Rate cannot be less than ${MIN_PERCENT_COLLATERAL}.`,
      (value) =>
        value?.lte(MAX_PERCENT_COLLATERAL) ||
        `Collateral Rate cannot be more than ${MAX_PERCENT_COLLATERAL}.`,
    ],
  },
  counterparty: { rules: [(value) => !isNewAgreement.value || required(value)] },
  settlementType: {
    rules: [
      required,
      () => ({
        result: true,
        trigger: [
          'collateralType',
          'pricingCurrency',
          'collateralCurrency',
          'collateralMethod',
          'dividendRequired',
          'billingCurrency',
          'deliveryType',
          'termType',
        ],
      }),
    ],
  },
  priceRoundingRules: { rules: [required] },
  collateralType: {
    rules: [
      (value) => value === CollateralType.CASH || checkForNSCCError('Cash'),
      (value) => value === CollateralType.CASH || checkForOCCError('Cash'),
      () => ({
        result: true,
        trigger: ['collateralMethod', 'deliveryType', 'collateralCurrency'],
      }),
    ],
  },
  pricingCurrency: {
    rules: [(value) => value === Currency.USD || checkForNSCCError('USD')],
  },
  collateralCurrency: {
    rules: [
      (value) => value === Currency.USD || checkForCashCollateralError('USD'),
      (value) => value === Currency.USD || checkForNSCCError('USD'),
      (value) => value === Currency.USD || checkForOCCError('USD'),
    ],
  },
  billingCurrency: {
    rules: [
      (value) => value === Currency.USD || checkForNSCCError('USD'),
      (value) => value === Currency.USD || checkForOCCError('USD'),
    ],
  },
  deliveryType: {
    rules: [(value) => value === DeliveryType.DVP || checkForCashCollateralError('DVP')],
  },
  collateralMethod: {
    rules: [
      required,
      (value) =>
        value === CollateralMethod.TITLE_TRANSFER || checkForCashCollateralError('Title Transfer'),
    ],
  },
  dividendRequired: {
    rules: [
      (value) =>
        (value && new Decimal(value).eq(NSCC_DIVIDEND_REQUIRED)) || checkForNSCCError('100'),
      (value) =>
        (value && new Decimal(value).eq(NSCC_DIVIDEND_REQUIRED)) || checkForOCCError('100'),
    ],
  },
  termType: {
    rules: [
      required,
      (value) =>
        value === TermType.NONE
          ? // reset "required" state for these fields
            { result: true, trigger: ['termStartDate', 'termEndDate'] }
          : true,
      (value) => value === TermType.NONE || checkForNSCCError('None'),
      (value) => value === TermType.NONE || checkForOCCError('None'),
    ],
  },
  termStartDate: {
    rules: [
      (value) => ({
        result:
          agreementRequest.value?.termType === TermType.NONE ||
          !!value ||
          'Required when Term Type is set',
        trigger: termEndDate.value === null ? [] : ['termEndDate'],
      }),
    ],
  },
  termEndDate: {
    rules: [
      (value) =>
        agreementRequest.value?.termType === TermType.NONE ||
        !!value ||
        'Required when Term Type is set',
      (value) =>
        Promise.resolve(
          !value ||
            !agreementRequest.value?.termStartDate ||
            +new Date(value) > +new Date(agreementRequest.value?.termStartDate) ||
            'Should be after Term Start Date'
        ),
      (value) =>
        Promise.resolve(!value || +new Date(value) > +new Date() || "Can't be in the past"),
    ],
  },
  rwaBucket: {
    rules: [(value) => !value || value.length <= 40 || 'Must have at most 40 characters'],
  },
  bookingAccount: {
    rules: [(value) => !value || value.length <= 20 || 'Must have at most 20 characters'],
  },
  tradeType: {
    rules: [(value) => !value || value.length <= 30 || 'Must have at most 30 characters'],
  },
  profitCenter: {
    rules: [(value) => !value || value.length <= 4 || 'Must have at most 4 characters'],
  },
});
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="900"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    scrollable
    :value="agreementRequest"
    @click:outside="closeIfNoChanges"
    @keydown.esc="closeIfNoChanges"
    @shortkey.native="closeIfNoChanges"
  >
    <VForm v-if="agreementRequest" novalidate @submit.prevent>
      <VCard>
        <VBtn class="close-icon" icon @click="closeDialog">
          <VIcon>mdi-close</VIcon>
        </VBtn>
        <VCardTitle class="d-flex align-center flex-grow-1">
          <span class="headline">
            {{ isMessage(agreementRequest, CreateAgreementRequestSchema) ? 'New' : 'Edit' }}
            Agreement
          </span>
          <span
            v-if="isMessage(agreementRequest, ModifyAgreementRequestSchema)"
            class="text--primary headline-2 ml-auto"
          >
            {{ agreementRequest.id }}
          </span>
        </VCardTitle>

        <div class="form-grid">
          <div class="col-span-2 form-group">
            <template v-if="isMessage(agreementRequest, ModifyAgreementRequestSchema)">
              <VTextField
                label="Side"
                readonly
                tabindex="-1"
                :value="source && formatSide(source?.side)"
              ></VTextField>
              <CompanyInfoDisplay
                v-if="source"
                :display-id="source.counterpartyId"
                label="Counterparty"
                tabindex="-1"
              />
            </template>
            <template v-else-if="isMessage(agreementRequest, CreateAgreementRequestSchema)">
              <VSelect
                v-model="agreementRequest.side"
                v-bind="rules.side"
                :autofocus="agreementRequest.side === SIDE_NON_SELECTED"
                data-required
                data-test-id="agreement-side"
                item-text="name"
                item-value="id"
                :items="availableSides"
                label="Side"
              ></VSelect>
              <CounterpartySearch
                v-model="agreementCounterparty"
                v-bind="rules.counterparty"
                :bilateral="agreementRequest.settlementType === SettlementType.BILATERAL"
                data-required
                data-test-id="agreement-counterparty-search"
                :exclude-company-id="authStore.user?.companyID"
                label="Counterparty"
                :side="counterpartySearchSide"
              />
            </template>
            <VSelect
              v-model="agreementRequest.settlementType"
              v-bind="rules.settlementType"
              data-required
              data-test-id="agreement-settlement-type"
              item-disabled="disabled"
              item-text="name"
              item-value="id"
              :items="settlementTypeOptions"
              label="Settlement Type"
            ></VSelect>

            <VTextField
              v-model.trim="agreementRequest.shortName"
              :autofocus="
                'side' in agreementRequest &&
                agreementRequest.side !== SIDE_NON_SELECTED &&
                !agreementRequest.shortName
              "
              class="col-span-3"
              v-bind="rules.shortName"
              data-required
              data-test-id="agreement-short-name"
              hint="Note: Short Name is only visible to you and will not be shared with your counterparty"
              label="Agreement Short Name"
            ></VTextField>

            <VTextField
              v-model="agreementRequest.bookingAccount"
              v-bind="rules.bookingAccount"
              auto-grow
              class="col-start-1"
              data-test-id="agreement-booking-account"
              label="Booking Account"
            />
            <VTextField
              data-test-id="agreement-counterparty-booking-account"
              label="Counterparty Booking Account"
              readonly
              tabindex="-1"
              :value="source?.counterpartyBookingAccount"
            />
            <VTextField
              v-model="agreementRequest.tradeType"
              data-test-id="agreement-trade-type"
              v-bind="rules.tradeType"
              label="Trade Type"
            />
          </div>

          <span class="grid-subtitle">Collateral</span>
          <div class="form-group">
            <VSelect
              v-model="agreementRequest.collateralType"
              data-test-id="agreement-collateral-type"
              item-text="name"
              item-value="id"
              :items="collateralTypeOptions"
              label="Collateral Type"
              v-bind="rules.collateralType"
              :readonly="
                isValueValidAndReadonly(agreementRequest.collateralType, CollateralType.CASH)
              "
              tabindex="-1"
            ></VSelect>
            <VSelect
              v-model="agreementRequest.pricingCurrency"
              data-test-id="agreement-pricing-currency"
              item-text="name"
              item-value="id"
              :items="pricingCurrencyOptions"
              label="Pricing Currency"
              v-bind="rules.pricingCurrency"
              :readonly="isSettlementTypeNSCC && agreementRequest.pricingCurrency === Currency.USD"
              tabindex="-1"
            ></VSelect>
            <NumericInput
              v-model="percentCollateral"
              v-bind="rules.percentCollateral"
              data-required
              data-test-id="agreement-percent-collateral"
              label="Collateral Rate"
              :max="MAX_PERCENT_COLLATERAL"
              :min="MIN_PERCENT_COLLATERAL"
              suffix="%"
              type="integer"
            ></NumericInput>
            <VSelect
              v-if="agreementRequest.collateralType == CollateralType.CASH"
              v-model="agreementRequest.collateralCurrency"
              data-test-id="agreement-collateral-currency"
              item-text="name"
              item-value="id"
              :items="currencyOptions"
              label="Collateral Currency"
              v-bind="rules.collateralCurrency"
              :readonly="isValueValidAndReadonly(agreementRequest.collateralCurrency, Currency.USD)"
              tabindex="-1"
            ></VSelect>
            <div v-else></div>
            <VSelect
              v-model="agreementRequest.deliveryType"
              data-test-id="agreement-delivery-type"
              v-bind="rules.deliveryType"
              item-text="name"
              item-value="id"
              :items="deliveryTypeOptions"
              label="Delivery Type"
              :readonly="
                isCollateralTypeCash ||
                isValueValidAndReadonly(agreementRequest.deliveryType, DeliveryType.DVP)
              "
              tabindex="-1"
            ></VSelect>
            <VSelect
              v-model="agreementRequest.collateralMethod"
              v-bind="rules.collateralMethod"
              data-required
              data-test-id="agreement-collateral-method"
              item-text="name"
              item-value="id"
              :items="collateralMethodOptions"
              label="Collateral Method"
              :readonly="
                isCollateralTypeCash ||
                isValueValidAndReadonly(
                  agreementRequest.collateralMethod,
                  CollateralMethod.TITLE_TRANSFER
                )
              "
            ></VSelect>
          </div>

          <span class="grid-subtitle">Billing and Dividends</span>
          <div class="form-group">
            <NumericInput
              v-model="dividendRequired"
              v-bind="rules.dividendRequired"
              data-test-id="agreement-dividend-required"
              label="Dividend Required"
              :min="0"
              :precision="2"
              :readonly="isValueValidAndReadonly(dividendRequired, NSCC_DIVIDEND_REQUIRED)"
              suffix="%"
              tabindex="-1"
              type="decimal"
            />
            <VSelect
              v-model="agreementRequest.billingCurrency"
              data-test-id="agreement-billing-currency"
              item-text="name"
              item-value="id"
              :items="currencyOptions"
              label="Billing Currency"
              v-bind="rules.billingCurrency"
              :readonly="isValueValidAndReadonly(agreementRequest.billingCurrency, Currency.USD)"
              tabindex="-1"
            ></VSelect>
            <VSelect
              v-model="roundingRule"
              v-bind="rules.priceRoundingRules"
              data-required
              data-test-id="agreement-price-rounding-rule"
              item-disabled="disabled"
              item-text="name"
              item-value="id"
              :items="availableRoundingRules"
              label="Price Rounding Rules"
            ></VSelect>
          </div>

          <span class="grid-subtitle">Term</span>
          <div class="form-group">
            <VSelect
              v-model="agreementRequest.termType"
              v-bind="rules.termType"
              data-test-id="agreement-term-type"
              item-text="name"
              item-value="id"
              :items="availableTermTypes"
              label="Term Type"
              :readonly="isValueValidAndReadonly(agreementRequest.termType, TermType.NONE)"
              :tabindex="agreementRequest.termType === TermType.NONE && -1"
            ></VSelect>
            <TextDatePicker
              v-model="termStartDate"
              clearable
              v-bind="rules.termStartDate"
              :data-required="agreementRequest.termType !== TermType.NONE"
              data-test-id="agreement-term-start-date"
              :disabled="agreementRequest.termType === TermType.NONE"
              :hint="
                agreementRequest.termType === TermType.NONE
                  ? 'Disabled when Term Type is None'
                  : undefined
              "
              label="Term Start Date"
              :tabindex="agreementRequest.termType === TermType.NONE && -1"
              @blur="validate('termStartDate')"
            />
            <TextDatePicker
              v-model="termEndDate"
              clearable
              v-bind="rules.termEndDate"
              :data-required="agreementRequest.termType !== TermType.NONE"
              data-test-id="agreement-term-end-date"
              :disabled="agreementRequest.termType === TermType.NONE"
              :hint="
                agreementRequest.termType === TermType.NONE
                  ? 'Disabled when Term Type is None'
                  : undefined
              "
              label="Term End Date"
              :tabindex="agreementRequest.termType === TermType.NONE && -1"
              @blur="validate('termEndDate')"
            />
          </div>

          <span class="grid-subtitle">Balance Targets</span>
          <div class="form-group">
            <NumericInput
              v-model="balanceTarget"
              clearable
              data-test-id="agreement-balance-target"
              label="Balance Target"
              :min="0"
              :precision="2"
              tabindex="-1"
              type="decimal"
            />
            <NumericInput
              v-model="balanceTargetVariance"
              clearable
              data-test-id="agreement-balance-target-variance"
              label="Balance Target Variance"
              :min="0"
              :precision="2"
              suffix="%"
              tabindex="-1"
              type="decimal"
            />
          </div>

          <span class="grid-subtitle">Misc</span>
          <div class="form-group">
            <VTextField
              v-model="agreementRequest.rwaBucket"
              v-bind="rules.rwaBucket"
              data-test-id="agreement-rwa-bucket"
              label="RWA Bucket"
              tabindex="-1"
            />
            <VSelect
              v-model="agreementRequest.settlementSystem"
              clearable
              :data-required="
                isSettlementTypeNSCC &&
                !isValueValidAndReadonly(
                  agreementRequest.settlementSystem,
                  SettlementSystem.PROVABLE_MARKET
                )
              "
              data-test-id="agreement-settlement-system"
              item-text="name"
              item-value="id"
              :items="settlementSystemOptions"
              label="Settlement System"
              :readonly="
                isSettlementTypeNSCC &&
                agreementRequest.settlementSystem === SettlementSystem.PROVABLE_MARKET
              "
              tabindex="-1"
            ></VSelect>
            <VTextField
              v-model="profitCenter"
              data-test-id="agreement-profit-center"
              label="Profit Center"
              tabindex="-1"
              v-bind="rules.profitCenter"
            ></VTextField>
          </div>
        </div>
        <VCardActions>
          <div class="d-flex flex-grow-1 justify-space-between">
            <VBtn color="secondary" tabindex="-1" @click="closeDialog">Cancel</VBtn>
            <VBtn
              color="success"
              :disabled="isValidating"
              :loading="isValidating"
              @click="validateAndSubmit(() => agreementRequest && save(agreementRequest))"
            >
              Save
            </VBtn>
          </div>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
.close-icon {
  top: 0.4rem;
  right: 0.4rem;
  position: absolute;
}

.form-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  padding: 0 2rem 1rem;
  margin-top: 1rem;
  overflow-y: auto;

  & > * {
    margin-top: 0;
  }

  & > {
    .grid-subtitle {
      color: #6fedc7;
      grid-column: 1;
    }
    .grid-subtitle + .form-group {
      grid-column: 2;
    }
    .form-group {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;

      // Active labels occupy more space on top, so adjusting the margin accordingly
      & > :has(.v-label) {
        interpolate-size: allow-keywords;
        transition: margin ease-in-out 300ms;
      }
      &:not(:has(.v-label--active)) > :has(.v-label) {
        margin-top: -18px !important;
      }

      ::v-deep {
        // Error messages occupy more space on bottom, so adjusting their height accordingly
        .v-text-field__details {
          interpolate-size: allow-keywords;

          transition:
            height ease-in-out 300ms,
            min-height ease-in-out 300ms;
          overflow: clip;

          &:not(:has(.v-messages__wrapper:empty)) {
            height: auto;
            min-height: 28px;
          }
          &:has(.v-messages__wrapper:empty) {
            min-height: 0;
            height: 0;
          }
        }
      }
    }
  }
}

.col-start-1 {
  grid-column-start: 1;
}
.col-span-2 {
  grid-column: span 2 / span 2;
}
.col-span-3 {
  grid-column: span 3 / span 3;
}
</style>
